/*
 * Edge Homepage JavaScript
 * Migrated from no-vue/src/homepage-edge.js to Vite + Tailwind 4 setup
 */

// import Lottie from 'lottie-web';
import './homepage.css';

// Import shared utilities
import '../shared/topbar.js';
import '../shared/coupon.js';
import '../shared/mobile-menu.js';
import '../shared/mega-menu.js';

// Homepage Edge specific functionality
class HomepageEdge {
  constructor() {
    this.init();
  }

  init() {
    this.setupHeroOffset();
    this.setupAnimations();
    this.setupScrollEffects();
    // this.setupLottieAnimations(); // Lottie not imported
    this.setupOrganizationsCarousel();
  }

  /*
   * Publish how far the hero sits below the top of the page as --hero-offset.
   *
   * Below `md` the nav is sticky (so it takes up flow) and the coupon banner
   * pads the page, which together push the hero down. The hero subtracts this
   * offset from 100svh so its bottom lands on the fold instead of below it.
   */
  setupHeroOffset() {
    const hero = document.querySelector('.hero-edge');
    if (!hero) {
      return;
    }

    const setHeroOffset = () => {
      const offset = hero.getBoundingClientRect().top + window.scrollY;
      document.documentElement.style.setProperty('--hero-offset', `${offset}px`);
    };

    setHeroOffset();

    // Catches viewport resizes and the coupon banner being closed
    new ResizeObserver(setHeroOffset).observe(document.body);
  }

  createFloatingAnimation(element) {
    // Set initial position
    element.style.transform = 'translateY(0px)';
    element.style.transition = 'transform 1.2s ease-in-out';
    
    let isMovingUp = true;
    const moveDistance = 15; // 15px movement
    const animationDuration = 1200; // 1.2 seconds
    
    function animate() {
      if (isMovingUp) {
        element.style.transform = `translateY(-${moveDistance}px)`;
      } else {
        element.style.transform = 'translateY(0px)';
      }
      isMovingUp = !isMovingUp;
    }
    
    // Start the animation loop
    setInterval(animate, animationDuration);
  }

  setupAnimations() {
    // Hero arrow animation for a gentle floating effect
    const heroArrow = document.querySelector('.hero-arrow');
    if (heroArrow) {
      this.createFloatingAnimation(heroArrow);
    }

    // Intersection Observer for fade-in animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const fadeInObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in-visible');
          fadeInObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Observe feature items
    document.querySelectorAll('.feature-item').forEach(item => {
      item.classList.add('fade-in');
      fadeInObserver.observe(item);
    });

    // Observe feature cards with stagger
    document.querySelectorAll('.feature-card').forEach((card, index) => {
      card.classList.add('fade-in');
      card.style.transitionDelay = `${index * 200}ms`;
      fadeInObserver.observe(card);
    });
  }

  setupScrollEffects() {
    // Parallax effect for hero background
    const heroBackground = document.querySelector('.hero-background');
    const moon = document.querySelector('.js-moon');
    const dashboardImage = document.querySelector('.js-dashboard-parallax');

    if (heroBackground || moon || dashboardImage) {
      let ticking = false;

      function updateParallax() {
        const scrolled = window.scrollY;

        // Hero background moves slower (0.5 speed)
        if (heroBackground) {
          const bgSpeed = 0.5;
          heroBackground.style.transform = `translateY(${scrolled * bgSpeed}px)`;
        }

        // Moon moves faster in opposite direction for depth effect
        if (moon) {
          const moonSpeed = 0.6; // Negative for opposite direction
          const moonRotation = scrolled * -0.02; // Slight rotation for added effect
          moon.style.transform = `translateY(${scrolled * moonSpeed}px) rotate(${moonRotation}deg)`;
        }

        // Dashboard image parallax effect
        if (dashboardImage) {
          // Get the dashboard section position
          const dashboardSection = dashboardImage.closest('section');
          if (dashboardSection) {
            const sectionTop = dashboardSection.offsetTop;
            const sectionHeight = dashboardSection.offsetHeight;
            const windowHeight = window.innerHeight;

            // Calculate if dashboard is in viewport
            if (scrolled + windowHeight > sectionTop && scrolled < sectionTop + sectionHeight) {
              // Calculate relative position within viewport
              const relativeScroll = scrolled - sectionTop + windowHeight;
              const dashboardSpeed = 0.05; // Negative for upward movement, subtle effect
              const translateY = relativeScroll * dashboardSpeed;

              dashboardImage.style.transform = `translateY(${translateY}px)`;
            }
          }
        }

        ticking = false;
      }

      window.addEventListener('scroll', () => {
        if (!ticking) {
          window.requestAnimationFrame(updateParallax);
          ticking = true;
        }
      });
    }

    // Image preloading for sections
    const preloadObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Find all images in the section
          const images = entry.target.querySelectorAll('img[loading="lazy"]');
          images.forEach(img => {
            // Force load the image by setting loading to eager
            img.loading = 'eager';

            // Preload the current viewport's image from srcset
            if (img.srcset) {
              const currentWidth = window.innerWidth * (window.devicePixelRatio || 1);
              const srcsetPairs = img.srcset.split(',').map(src => {
                const parts = src.trim().split(' ');
                const url = parts[0];
                const width = parts[1] ? parseInt(parts[1]) : currentWidth;
                return { url, width };
              });

              const bestMatch = srcsetPairs.reduce((prev, curr) => {
                if (curr.width >= currentWidth && (!prev || curr.width < prev.width)) {
                  return curr;
                }
                return prev;
              }, srcsetPairs[srcsetPairs.length - 1]);

              if (bestMatch && bestMatch.url) {
                const link = document.createElement('link');
                link.rel = 'preload';
                link.as = 'image';
                link.href = bestMatch.url;
                document.head.appendChild(link);
              }
            }
          });

          // Also preload picture sources
          const pictures = entry.target.querySelectorAll('picture');
          pictures.forEach(picture => {
            const sources = picture.querySelectorAll('source');
            let matchedSource = null;

            for (const source of sources) {
              const media = source.media;
              if (!media || window.matchMedia(media).matches) {
                matchedSource = source;
                break;
              }
            }

            if (matchedSource && matchedSource.srcset) {
              const currentWidth = window.innerWidth * (window.devicePixelRatio || 1);
              const srcsetPairs = matchedSource.srcset.split(',').map(src => {
                const parts = src.trim().split(' ');
                const url = parts[0];
                const descriptor = parts[1] || '1x';
                const multiplier = descriptor.endsWith('x') ?
                  parseFloat(descriptor) :
                  (parseInt(descriptor) / currentWidth);
                return { url, multiplier };
              });

              const dpr = window.devicePixelRatio || 1;
              const bestMatch = srcsetPairs.reduce((prev, curr) => {
                if (Math.abs(curr.multiplier - dpr) < Math.abs(prev.multiplier - dpr)) {
                  return curr;
                }
                return prev;
              }, srcsetPairs[0]);

              if (bestMatch && bestMatch.url) {
                const link = document.createElement('link');
                link.rel = 'preload';
                link.as = 'image';
                link.href = bestMatch.url;
                link.type = matchedSource.type || '';
                document.head.appendChild(link);
              }
            }
          });

          preloadObserver.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0,
      rootMargin: '200px 0px' // Start preloading 200px before section comes into view
    });

    // Section fade-in effects using Intersection Observer
    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('section-visible');
        }
      });
    }, {
      threshold: 0.1
    });

    document.querySelectorAll('section').forEach(section => {
      section.classList.add('section-fade');
      // First observe for preloading
      preloadObserver.observe(section);
      // Then observe for animation
      sectionObserver.observe(section);
    });
  }

  setupLottieAnimations() {
    // Features animation
    const featuresContainer = document.getElementById('features-animation');
    if (featuresContainer) {
      Lottie.loadAnimation({
        container: featuresContainer,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        path: '/wp-content/themes/pressidium-com/assets/animations/We_Are_Global_2024_1600x1000px_F.json'
      });
    }

    // World map animation
    const worldMapContainer = document.getElementById('world-map-animation');
    if (worldMapContainer) {
      const worldMapAnim = Lottie.loadAnimation({
        container: worldMapContainer,
        renderer: 'svg',
        loop: true,
        autoplay: false,
        path: '/wp-content/themes/pressidium-com/assets/animations/We_Are_Global_Updated.json'
      });

      // Play animation when in viewport using Intersection Observer
      const mapObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            worldMapAnim.play();
          } else {
            worldMapAnim.pause();
          }
        });
      }, {
        threshold: 0.5
      });

      mapObserver.observe(worldMapContainer);
    }
  }

  setupOrganizationsCarousel() {
    const carousel = document.getElementById('organizations-carousel');
    if (!carousel) return;

    const logoSlots = carousel.querySelectorAll('.logo-slot');

    // Initialize each logo slot with random rotation
    logoSlots.forEach((slot, slotIndex) => {
      const logos = slot.querySelectorAll('.logo-item');
      let currentLogoIndex = 0;

      // Show the first logo initially
      logos[currentLogoIndex].classList.add('active');

      // Function to rotate logos in this slot
      function rotateLogos() {
        // Hide current logo
        logos[currentLogoIndex].classList.remove('active');

        // Move to next logo
        currentLogoIndex = (currentLogoIndex + 1) % logos.length;

        // Show next logo
        logos[currentLogoIndex].classList.add('active');

        // Schedule next rotation with random delay
        scheduleNextRotation();
      }

      // Schedule rotation with random delay between 3-6 seconds
      function scheduleNextRotation() {
        const randomDelay = 3000 + Math.random() * 3000; // 3-6 seconds
        setTimeout(rotateLogos, randomDelay);
      }

      // Start rotation after initial random delay
      const initialDelay = 2000 + Math.random() * 2000 * slotIndex; // Stagger initial rotations
      setTimeout(scheduleNextRotation, initialDelay);
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new HomepageEdge();
});
